import { useEffect, useMemo, useRef, useState } from 'react';

import { useTracking } from 'hooks/analytic-tracking';
import { useDocumentTitle } from 'hooks/document-title';
import { useLinkedinCallback } from 'hooks/linkedin-callback';
import { useRefereeUploadActions } from 'hooks/upload-actions';
import { useUserAgent } from 'hooks/user-agent';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';

import AlreadyCompletedForm from 'components/AlreadyCompletedForm/AlreadyCompletedForm';
import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import RefereeFormWrapper from 'components/Form/RefereeForm/RefereeFormWrapper';
import { FormMessage } from 'components/FormComponents';
import LinkedinVerification from 'components/LinkedinVerification/LinkedinVerification';
import { Loading } from 'components/Loading/Loading';
import { Modal } from 'components/Modal/Modal';

import { useLazyGetLinkedinProfileQuery } from 'api/linkedin-api';
import {
  useGetCompanyCheckConfigByTokenQuery,
  useGetRefereeBrandByTokenQuery,
  useGetRefereeByTokenQuery,
  useLazyGetRefereeFormByTokenQuery,
  useLazyVerifyRefereeQuery,
  useSaveFollowUpQuestionMutation,
  useSaveResponseMutation,
  useSaveUtmSourceMutation,
} from 'api/referees-api';

import { useQueryParams } from '../../hooks/query-params';
import { CustomButton } from '../CandidateApp/components/CustomButton/CustomButton';
import { CustomLink } from '../CandidateApp/components/CustomLink/CustomLink';
import styles from './RefereeFormSubmission.module.scss';
import { TypeformForm } from './TypeformForm/TypeformForm';

export const NewRefereeFormSubmission = () => {
  useDocumentTitle();

  const navigate = useNavigate();
  const { token, answered_on_behalf_of } = useParams();

  const topRef = useRef();

  const { user_agent } = useUserAgent();
  const [utm_source] = useQueryParams(['utm_source']);

  const { data: brand, isLoading: loadingBranding } = useGetRefereeBrandByTokenQuery(token);
  const { data: checkConfig, isLoading: loadingCheckConfig } =
    useGetCompanyCheckConfigByTokenQuery(token);
  const {
    data: referee,
    error: refereeError,
    isLoading: loadingReferee,
  } = useGetRefereeByTokenQuery(token);

  if (refereeError) {
    navigate(`/referee_not_found_error?message=${refereeError?.data?.error?.log}`);
  }

  const { getUpload, signUpload, createUpload, deleteUpload } = useRefereeUploadActions(token);

  const [saveUtmSource] = useSaveUtmSourceMutation();
  const [getRefereeForm] = useLazyGetRefereeFormByTokenQuery();
  const [getLinkedinProfile] = useLazyGetLinkedinProfileQuery();
  const [verifyReferee] = useLazyVerifyRefereeQuery();
  const [saveResponse] = useSaveResponseMutation();
  const [saveFollowUpResponse] = useSaveFollowUpQuestionMutation();
  const [linkedinProfile, setLinkedinProfile] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showIdVerification, setShowIdVerification] = useState(false);
  const [showAlreadyCompleted, setShowAlreadyCompleted] = useState(false);
  const [showIntroduction, setShowIntroduction] = useState(true);

  // Either one or the other
  const [typeformParams, setTypeformParams] = useState();
  const [form, setForm] = useState();
  const [formParams, setFormParams] = useState({});
  const [locale, setLocale] = useState('en_GB');

  // Save UTM
  useEffect(() => {
    saveUtmSource({ token, utm_source });
  }, []);

  useEffect(() => {
    if (referee?.completed_at) setShowAlreadyCompleted(true);

    if (!referee?.id || process.env.REACT_APP_ENV !== 'prod') {
      return;
    }

    window.Intercom('shutdown');

    if (!referee?.company?.show_support_widget) {
      return;
    }

    window.Intercom('boot', {
      app_id: 'yh3o8vqn',
      name: referee.name,
      email: referee.email,
      user_id: `referee-${referee.id}-${process.env.REACT_APP_ENV}`,
      user_type: 'referee',
      created_at: moment(referee.created_at, 'DD/MM/YYYY').unix(),
    });
  }, [referee]);

  useLinkedinCallback(
    async code => {
      setLoading(true);
      const result = await getLinkedinProfile(code).unwrap();
      getForm(result);
    },
    [referee],
  );

  const getForm = async linkedin_profile => {
    setLoading(true);
    setShowIdVerification(false);
    if (referee?.form_id) {
      setLinkedinProfile(linkedin_profile);
      const { form, params, locale } = await getRefereeForm(token).unwrap();
      setForm(form);
      setFormParams(params);
      setLocale(locale);
    } else {
      const result = await verifyReferee({ token, linkedin_profile, user_agent }).unwrap();
      setTypeformParams(result);
    }
    setLoading(false);
  };

  const closeIntroduction = async () => {
    setShowIntroduction(false);

    if (checkConfig?.disabled_linkedin_verification) {
      getForm('');
    } else {
      setShowIdVerification(true);
    }
  };

  const followUpQuestion = useMemo(() => {
    if (brand?.follow_up_question_active) {
      return {
        id: 'follow_up_question',
        type: 'yes_no',
        text: brand.follow_up_question,
        required: true,
        group: 'follow_up_question',
      };
    }
    return null;
  }, [brand]);

  const questions = useMemo(() => {
    if (followUpQuestion) {
      return [...(form?.fields || []), followUpQuestion];
    }
    return form?.fields;
  }, [form, followUpQuestion]);

  const saveAnswers = async answers => {
    setLoading(true);

    const { follow_up_question, ...mainAnswers } = answers;

    const { error } = await saveResponse({
      token,
      params: {
        user_agent,
        linkedin_profile: linkedinProfile,
        answers: mainAnswers,
        ...(answered_on_behalf_of ? { answered_on_behalf_user_id: answered_on_behalf_of } : {}),
      },
    });

    if (error) {
      setError(true);
      setLoading(false);
      if (topRef?.current) {
        topRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      if (follow_up_question) {
        await saveFollowUpResponse({
          token,
          follow_up_answer: follow_up_question,
        });
      }

      if (brand.click_through_active && brand.click_through_referee_active)
        window.location.replace(brand.click_through_referee);
      else navigate(`/referee_completion/${token}`);
    }
  };

  return (
    <div className={styles.root} ref={topRef}>
      <Loading active={loading || loadingReferee || loadingCheckConfig || loadingBranding} />
      <AnimatedModal visible={showIntroduction} small noClose title="Introduction to Checkmate">
        <p className="u-margin-top--large">
          Checkmate is a secure verification platform designed to simplify the process for employers
          and employees. The privacy and security of your data is our highest priority, and we have
          several systems and processes to keep your information safe and secure.
          <br />
          <br />
          Checkmate is ISO27001 Certified and uses encrypted connections to ensure your privacy and
          confidentiality is maintained throughout this application. We are externally audited
          annually and commission regular independent tests to ensure the security of our system.
          <br />
          <br />
          Checkmate performs several employment checks and verification on behalf of employers; we
          will ask the required questions from the employer so you can provide a reference for the
          candidate.
          <br />
          <br />
          All information you supply is kept strictly confidential and is only accessible by
          authorised staff.
          <br />
          <br />
          Before proceeding, please read our{' '}
          <CustomLink
            brand={brand}
            href="https://www.checkmate.tech/utility/referee-consent-statement">
            Referee Consent Statement
          </CustomLink>
          ,{' '}
          <CustomLink brand={brand} href="https://www.checkmate.tech/utility/privacy-policy">
            Privacy Policy
          </CustomLink>{' '}
          and{' '}
          <CustomLink brand={brand} href="https://www.checkmate.tech/utility/terms-and-conditions">
            Terms and Conditions
          </CustomLink>{' '}
          here. By closing this window, you confirm that you have read, understood, and agreed to
          proceed.
          <br />
          <br />
          If you have any questions or concerns please contact our support team via email at{' '}
          <CustomLink brand={brand} href="mailto:team@checkmate.tech">
            team@checkmate.tech
          </CustomLink>
          .
        </p>
        <CustomButton brand={brand} onClick={closeIntroduction} className="u-margin-top--large">
          Close Window
        </CustomButton>
      </AnimatedModal>

      <Modal show={showIdVerification} disableClose fullWindow>
        <LinkedinVerification onSubmit={getForm} />
      </Modal>
      <Modal show={showAlreadyCompleted} disableClose>
        <AlreadyCompletedForm />
      </Modal>
      {typeformParams && (
        <TypeformForm
          params={typeformParams}
          onSubmit={() => navigate(`/referee_completion/${token}`)}
        />
      )}
      {form && (
        <>
          {error && (
            <FormMessage message="Something went wrong. Please try again. Contact us if the issue persist." />
          )}
          <RefereeFormWrapper
            formMessage={form?.message}
            questions={questions}
            logicJumps={form.logic_jumps}
            rules={form.rules}
            candidateId={referee?.candidate_id}
            textParams={formParams}
            locale={locale}
            getCandidateUpload={getUpload}
            deleteCandidateUpload={deleteUpload}
            signCandidateUpload={signUpload}
            createCandidateUpload={createUpload}
            brand={brand}
            onSubmit={saveAnswers}
          />
        </>
      )}
    </div>
  );
};

export default NewRefereeFormSubmission;
